import type { ForteXApi } from "@fortex/api";
import type { CatalogueEntry } from "../catalogue/types";
import { geojsonSource } from "./geojson";
import { leafletLayerSource } from "./leafletLayer";
import { createSpecialSource } from "./special";
import type { GoogleMutantLoader } from "./special";
import { tileSource } from "./tile";
import { createTilesSource } from "./tiles";
import { createTrackSource } from "./track";
import type { LayerSource, SourceContext, SourceFactory, SourceRegistry } from "./types";
import { createVectorSource } from "./vector";

export type SourceKind = "tile" | "special" | "tiles" | "vector" | "geojson" | "leaflet" | "track";

export function sourceKindOf(entry: CatalogueEntry): SourceKind | null {
  if (entry.origin === "host") {
    const kind = entry.settings.hostKind;

    return kind === "geojson" || kind === "leaflet" || kind === "track" ? kind : null;
  }

  switch (entry.kind) {
    case "group":
      return null;
    case "tile":
      return "tile";
    case "special":
      return "special";
    case "folder":
    case "geoobject":
      return entry.root === "maps" ? "tiles" : "vector";
  }
}

export function createSourceRegistry(): SourceRegistry {
  const factories = new Map<string, SourceFactory>();

  return {
    register: (kind, factory) => {
      factories.set(kind, factory);
    },
    has: kind => factories.has(kind),
    create: (entry: CatalogueEntry, context: SourceContext): LayerSource | null => {
      const kind = sourceKindOf(entry);

      if (kind === null) return null;

      const factory = factories.get(kind);

      return factory ? factory(entry, context) : null;
    }
  };
}

export type DefaultRegistryOptions = {
  extra?: Record<string, SourceFactory>;
  googleLoader?: GoogleMutantLoader;
  colorIndex?: (entry: CatalogueEntry) => number;
};

export function createDefaultRegistry(api: ForteXApi, options: DefaultRegistryOptions = {}): SourceRegistry {
  const registry = createSourceRegistry();

  registry.register("tile", tileSource);
  registry.register("special", createSpecialSource(options.googleLoader));
  registry.register("tiles", createTilesSource(api));
  registry.register("vector", createVectorSource(api, options.colorIndex));
  registry.register("geojson", geojsonSource);
  registry.register("leaflet", leafletLayerSource);
  registry.register("track", createTrackSource(api));

  for (const [kind, factory] of Object.entries(options.extra ?? {})) registry.register(kind, factory);

  return registry;
}
